import { getCustomAttrCustomFacetCampaign } from '@utils/getCustomAttrCustomFacetCampaign'
import { getFullImageUrl } from '@utils/getFullImageUrl'
import { getProductTitle } from '@utils/getProductTitle'
import type { FC } from 'react'
import { useMemo } from 'react'
import Link from 'next/link'
import { Button } from '@ui/components/Button'
import type { ProductItemBaseProps } from '../types'
import { ProductImage } from '../ProductImage'

import { AppRoutes } from 'app/routes'
import { ButtonArrow } from '@ui/icons/button'
import { useCart } from '@hooks/useCart'
import clsx from 'clsx'
import { Badge } from '../Badge'
import { getFormattedPrice } from '@utils/price'
import s from './ProductTableListItem.module.scss'
import { BaseButton } from '@ui/components/BaseButton'
import { CustomFacetNumber } from '@model/product/CustomFacet'
import { useSpecialPrice } from '@hooks/product'
import { usePriceInfo } from '@hooks/usePriceInfo'
import { useCurrency } from '@hooks/useCurrency'
import { useTranslate } from '@hooks/useTranslation'
import { getSlug } from '@utils/slugs'
import { isSkuForbiddenToAddToCart } from '@utils/product-utils'
import { useInitialData } from '@context/initialDataContext'

export type ProductTableListItemProps = ProductItemBaseProps

export const ProductTableListItem: FC<ProductTableListItemProps> = ({ product }) => {
  const { image: imageRelativePath, manufacturerName, name: title, sku, id, quantity, customAttrArray } = product

  const { translate } = useTranslate()

  const {
    initialData: { allPromotions },
  } = useInitialData()

  const priceInfo = usePriceInfo(product, allPromotions)
  const { currency } = useCurrency()
  const image = useMemo(() => getFullImageUrl(imageRelativePath?.replace('_big', '_medium')), [imageRelativePath])
  const productTitle = getProductTitle(product)
  const productSlug = getSlug(title, sku)
  const productHref = AppRoutes.productDetails.get(productSlug)
  const { addToCartWithSideEffects } = useCart('shopping cart')

  const handleAddToCart = async () => {
    await addToCartWithSideEffects({ productId: id, sku, quantity: 1 })
  }

  const isOutOfStock = !quantity || quantity < 1
  const campaign = getCustomAttrCustomFacetCampaign(customAttrArray)
  const normalPrice = priceInfo.normalPrice
  const specialPrice = useSpecialPrice(product) && priceInfo.specialPrice

  return (
    <div className={clsx('contents bg-white hover:bg-group-grey', s.row)}>
      <Link passHref href={productHref} className="">
        <ProductImage
          width="100"
          height="100"
          alt={product.sku}
          src={image}
          className="aspect-[2/1]"
          product={product}
          transformations="big"
        />
      </Link>

      <div className="flex flex-[1_1_auto] flex-row px-lg items-center overflow-hidden ">
        <div className="flex flex-col space-y-xs overflow-hidden overflow-ellipsis">
          {campaign.length !== 0 && (
            <div className="justify-self-start mr-auto">
              <Badge className="cws-mb-xs">{translate(campaign[0])}</Badge>
            </div>
          )}
          <p className="text-base font-normal leading-5">{manufacturerName}</p>
          <Link
            passHref
            href={productHref}
            className="text-base font-medium leading-5 max-h-10 overflow-hidden overflow-ellipsis"
          >
            {productTitle}
          </Link>
        </div>
      </div>

      <div className="flex border-l border-r border-group-grey border-dotted px-4 items-center">
        <div className="flex flex-1-auto flex-col gap-y-2 gap-x-4">
          {!!specialPrice ? (
            <>
              <div className="flex flex-row justify-between align-baseline items-baseline gap-x-2.5">
                <p className="text-sm leading-6 text-midnight font-normal text-left align-baseline border-b border-dotted border-midnight">
                  {translate('w2.product.detail.price.title')}
                </p>
                <p className="text-base leading-6 text-notice font-medium text-right align-baseline  border-b border-dotted border-notice">
                  {getFormattedPrice(specialPrice, currency)}
                </p>
              </div>
              <div className="flex flex-row justify-between align-baseline items-baseline gap-x-2.5">
                <p className="text-sm text-dark-grey font-normal text-left align-baseline">
                  {translate('w2.product.tile.price.normal')}
                </p>
                <p className="text-sm text-dark-grey font-medium text-right align-baseline">
                  {getFormattedPrice(normalPrice, currency)}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-row justify-between align-baseline items-baseline gap-x-2.5">
                <p className="text-sm text-midnight font-normal text-left align-baseline">
                  {translate('w2.product.tile.price.normal')}
                </p>
                <p className="text-base text-midnight font-medium text-right align-baseline">
                  {getFormattedPrice(normalPrice, currency)}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={clsx('flex flex-col px-4 items-stretch justify-center gap-y-1', !isOutOfStock && 'pt-2')}>
        {isOutOfStock ? (
          <>
            <Button size="sm" onClick={handleAddToCart}>
              {translate('product.tile.out.of.stock')}
            </Button>
          </>
        ) : (
          <>
            <BaseButton
              size="sm"
              onClick={handleAddToCart}
              className={clsx('text-sm', isSkuForbiddenToAddToCart(sku) && 'invisible')}
            >
              {translate('common.add.to.cart')}
            </BaseButton>
            <Link passHref href={productHref} className="flex justify-center">
              <Button size="sm" variant="text" className="">
                <span className="text-buoy-link font-medium text-sm leading-5 normal-case mr-1">
                  {translate('order.history.details.open.details')}
                </span>
                <ButtonArrow color="buoy-link" className="text-buoy-link" />
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  )
}

export default ProductTableListItem
